/**
 * routes/index.tsx — v.1-D §9.
 *
 * The complete ~85-route tree. Three protective rings:
 *   1. RouteErrorBoundary       (per-page error boundary via <L name="…">).
 *   2. ProtectedShell           (auth-required wrapper).
 *   3. AdminGate                (admin-only sub-tree wrapper).
 *
 * Every page is code-split via React.lazy(). Pages that exist today land
 * their real modules; pages produced by later CODE-phase tasks (T321..T361)
 * currently resolve to a shared <PlaceholderPage/> via an inline lazy
 * promise. When a subsequent task ships the real page, the corresponding
 * `lazy(...)` declaration is swapped from the inline placeholder to
 * `import('../pages/...')`. No other route-tree edits required.
 *
 * Why placeholder-by-inline instead of one-stub-file-per-route: stub files
 * would conflict with subsequent CODE-phase tasks whose `outputs` declare
 * exactly the same paths. The inline-placeholder pattern lets every task
 * own its file from creation rather than from edit.
 */
import React, { lazy, Suspense, type JSX, type ReactNode } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { ProtectedShell } from '../layouts/ProtectedShell';
import { AdminGate } from '../design/primitives/AdminGate';
import { RouteErrorBoundary } from '../design/primitives/RouteErrorBoundary';
import { PageSkeleton } from '../components/shared/PageSkeleton';

// ---------------------------------------------------------------------------
// Placeholder for surfaces produced by subsequent CODE-phase tasks (T321..T361).
// ---------------------------------------------------------------------------

function PlaceholderPage({ name }: { name: string }): JSX.Element {
  return (
    <main style={{ padding: 32 }}>
      <div
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 11,
          textTransform: 'uppercase',
          letterSpacing: 0.7,
          color: 'var(--muted)',
          marginBottom: 8,
        }}
      >
        Coming soon
      </div>
      <h1
        style={{
          fontFamily: 'var(--font-display)',
          fontSize: 28,
          marginTop: 0,
        }}
      >
        {name}
      </h1>
      <p style={{ color: 'var(--muted)', fontSize: 13, maxWidth: 480 }}>
        This surface lands in a subsequent Phase-3 CODE task. The route is
        wired so the navigation chrome behaves correctly today.
      </p>
    </main>
  );
}

/** Build a lazy-loaded placeholder for any not-yet-implemented surface. */
function placeholder(name: string) {
  const Component: React.FC = () => <PlaceholderPage name={name} />;
  Component.displayName = `Placeholder(${name})`;
  return lazy(() => Promise.resolve({ default: Component }));
}

// ---------------------------------------------------------------------------
// Real lazy imports — pages already shipped by T303..T311.
// ---------------------------------------------------------------------------

const SignInPage = lazy(() =>
  import('../pages/auth/SignInPage').then((m) => ({ default: m.SignInPage })),
);
const VerifyEmailPage = lazy(() =>
  import('../pages/auth/VerifyEmailPage').then((m) => ({
    default: m.VerifyEmailPage,
  })),
);
const ForgotPasswordPage = lazy(() =>
  import('../pages/auth/ForgotPasswordPage').then((m) => ({
    default: m.ForgotPasswordPage,
  })),
);
const LockedPage = lazy(() =>
  import('../pages/auth/LockedPage').then((m) => ({ default: m.LockedPage })),
);
const FirstTimeUserPage = lazy(() =>
  import('../pages/auth/FirstTimeUserPage').then((m) => ({
    default: m.FirstTimeUserPage,
  })),
);
const SessionExpiredPage = lazy(() =>
  import('../pages/auth/SessionExpiredPage').then((m) => ({
    default: m.SessionExpiredPage,
  })),
);
const OnboardingPage = lazy(() =>
  import('../pages/auth/OnboardingPage').then((m) => ({
    default: m.OnboardingPage,
  })),
);
const PrimitivesShowcasePage = lazy(() => import('../pages/PrimitivesShowcasePage'));

// ---------------------------------------------------------------------------
// Placeholder lazy refs — each will be swapped to a real import by a later task.
// ---------------------------------------------------------------------------

// Dashboard, Channels list/detail + 5 tabs — landed by T321, wired here by T324.
const DashboardPage = lazy(() =>
  import('../pages/DashboardPage').then((m) => ({ default: m.DashboardPage })),
);
const ChannelsListPage = lazy(() =>
  import('../components/channels/ChannelsListPage').then((m) => ({
    default: m.ChannelsListPage,
  })),
);
// /channels/new opens the CreateChannelModal inside ChannelsListPage; the
// modal-open state is driven by route match, so /channels/new mounts the
// same component as /channels.
const CreateChannelPage = lazy(() =>
  import('../components/channels/ChannelsListPage').then((m) => ({
    default: m.ChannelsListPage,
  })),
);
const ChannelDetailLayout = lazy(() =>
  import('../components/channels/ChannelDetailPage').then((m) => ({
    default: m.ChannelDetailPage,
  })),
);
const ChannelOverviewTab = lazy(() =>
  import('../components/channels/ChannelOverviewTab').then((m) => ({
    default: m.ChannelOverviewTab,
  })),
);
const ChannelScheduleTab = lazy(() =>
  import('../components/channels/ChannelScheduleTab').then((m) => ({
    default: m.ChannelScheduleTab,
  })),
);
const ChannelLiveTab = lazy(() =>
  import('../components/channels/ChannelLiveTab').then((m) => ({
    default: m.ChannelLiveTab,
  })),
);
const ChannelHealthTab = lazy(() =>
  import('../components/channels/ChannelHealthTab').then((m) => ({
    default: m.ChannelHealthTab,
  })),
);
const ChannelSettingsTab = lazy(() =>
  import('../components/channels/ChannelSettingsTab').then((m) => ({
    default: m.ChannelSettingsTab,
  })),
);
const ChannelPerformancePage = lazy(() =>
  import('../components/channels/ChannelPerformancePage').then((m) => ({
    default: m.ChannelPerformancePage,
  })),
);
// DevOps 2026-05-28: operator preview surface for channel-level playback.
// Points an <AssetPlayer> at oo-edge per-channel HLS endpoint.
const ChannelWatchPage = lazy(() =>
  import('../components/channels/ChannelWatchPage').then((m) => ({
    default: m.ChannelWatchPage,
  })),
);
// Phase 7 — encoder + go-live (T351).
const EncoderHelperPage = lazy(() =>
  import('../components/channels/EncoderHelperPage').then((m) => ({
    default: m.EncoderHelperPage,
  })),
);
const GoLivePage = lazy(() =>
  import('../components/channels/GoLivePage').then((m) => ({
    default: m.GoLivePage,
  })),
);
const DestinationsWizardPage = placeholder('Channel · Destinations');
// Phase 9 — channel lifecycle pages (T361).
const ChannelTransferPage = lazy(() =>
  import('../components/channels/ChannelTransferPage').then((m) => ({
    default: m.ChannelTransferPage,
  })),
);
const ChannelArchivePage = lazy(() =>
  import('../components/channels/ChannelArchivePage').then((m) => ({
    default: m.ChannelArchivePage,
  })),
);
const ChannelDuplicatePage = lazy(() =>
  import('../components/channels/ChannelDuplicatePage').then((m) => ({
    default: m.ChannelDuplicatePage,
  })),
);
// Library + ContentDetail + MarkerEditor — landed by T323, wired here by T324.
const LibraryPage = lazy(() =>
  import('../components/library/LibraryPage').then((m) => ({
    default: m.LibraryPage,
  })),
);
const UploadAssetPage = lazy(() =>
  import('../components/library/UploadAssetPage').then((m) => ({
    default: m.UploadAssetPage,
  })),
);
const IngestSourcesPage = lazy(() =>
  import('../components/library/IngestSourcesPage').then((m) => ({
    default: m.IngestSourcesPage,
  })),
);
const ContentDetailPage = lazy(() =>
  import('../components/library/ContentDetailPage').then((m) => ({
    default: m.ContentDetailPage,
  })),
);
const MarkerEditorPage = lazy(() =>
  import('../components/library/MarkerEditorPage').then((m) => ({
    default: m.MarkerEditorPage,
  })),
);
// Phase 6 library surfaces — landed by T345.
const AnalyseVideoPage = lazy(() =>
  import('../components/library/AnalyseVideoPage').then((m) => ({
    default: m.AnalyseVideoPage,
  })),
);
const EditMetadataPage = lazy(() =>
  import('../components/library/EditMetadataPage').then((m) => ({
    default: m.EditMetadataPage,
  })),
);
const AssetCaptionsPage = lazy(() =>
  import('../components/library/AssetCaptionsPage').then((m) => ({
    default: m.AssetCaptionsPage,
  })),
);
const ReplaceFilePage = placeholder('Library · Replace file');
const BulkCaptionUploadPage = lazy(() =>
  import('../components/library/BulkCaptionUploadPage').then((m) => ({
    default: m.BulkCaptionUploadPage,
  })),
);
const SmartSearchPage = lazy(() =>
  import('../components/library/SmartSearchPage').then((m) => ({
    default: m.SmartSearchPage,
  })),
);
const SmartBreakSuggestionsPage = lazy(() =>
  import('../components/library/SmartBreakSuggestionsPage').then((m) => ({
    default: m.SmartBreakSuggestionsPage,
  })),
);
// Same component handles both list + per-asset routes (different params).
const SmartBreakSuggestionsAssetPage = SmartBreakSuggestionsPage;
const ScenesPage = lazy(() =>
  import('../components/library/ScenesPage').then((m) => ({
    default: m.ScenesPage,
  })),
);
// Schedules + CreateSchedule + ScheduleEditor — landed by T323, wired here by T324.
const SchedulesPage = lazy(() =>
  import('../components/schedules/SchedulesPage').then((m) => ({
    default: m.SchedulesPage,
  })),
);
const CreateSchedulePage = lazy(() =>
  import('../components/schedules/CreateSchedulePage').then((m) => ({
    default: m.CreateSchedulePage,
  })),
);
const ScheduleEditorPage = lazy(() =>
  import('../components/schedules/ScheduleEditorPage').then((m) => ({
    default: m.ScheduleEditorPage,
  })),
);
const PublishingModalPage = placeholder('Schedules · Publish');
// Programme Guide Export — landed by T343 (Phase 5 Track B, §16.5).
const ProgrammeGuideExportPage = lazy(() =>
  import('../components/schedules/ProgrammeGuideExportPage').then((m) => ({
    default: m.ProgrammeGuideExportPage,
  })),
);
const TemplatesPage = lazy(() =>
  import('../components/library/TemplatesPage').then((m) => ({
    default: m.TemplatesPage,
  })),
);
const TemplateDetailPage = placeholder('Templates · Detail');
const GuidePage = lazy(() =>
  import('../components/schedules/GuidePage').then((m) => ({
    default: m.GuidePage,
  })),
);
// Feed Sources — landed by T343 (Phase 5 Track A, §16.5).
const FeedSourcesPage = lazy(() =>
  import('../components/editorial/FeedSourcesPage').then((m) => ({
    default: m.FeedSourcesPage,
  })),
);
const EditorialPage = lazy(() =>
  import('../components/editorial/EditorialPage').then((m) => ({
    default: m.EditorialPage,
  })),
);
// Editorial Inbox Item — landed by T341 (per-item review surface, §13.11).
const EditorialInboxItemPage = lazy(() =>
  import('../components/editorial/EditorialInboxItemPage').then((m) => ({
    default: m.EditorialInboxItemPage,
  })),
);
// Editorial Sync — landed by T343 (Phase 5 Track A, §16.5;
// editorialPipelineRetryWatcher freshness per Appendix D).
const EditorialSyncPage = lazy(() =>
  import('../components/editorial/EditorialSyncPage').then((m) => ({
    default: m.EditorialSyncPage,
  })),
);
// HMAC Test Tool — landed by T343 (Phase 5 Track A; carries E.5 verbatim).
const HmacTestToolPage = lazy(() =>
  import('../components/editorial/HmacTestToolPage').then((m) => ({
    default: m.HmacTestToolPage,
  })),
);
const CompliancePage = lazy(() =>
  import('../components/compliance/CompliancePage').then((m) => ({
    default: m.CompliancePage,
  })),
);
const BulkOpsPage = lazy(() =>
  import('../components/operations/BulkOpsPage').then((m) => ({
    default: m.BulkOpsPage,
  })),
);
// Publishing Destinations — landed by T343 (Phase 5 Track B, §16.5;
// replaces the v.1-C stub per Prompt 14.1).
const PublishingDestinationsPage = lazy(() =>
  import('../components/publishing/PublishingDestinationsPage').then((m) => ({
    default: m.PublishingDestinationsPage,
  })),
);
// Quota Rotation — landed by T343 (Phase 5 Track B; C.12 youtubeProject).
const QuotaRotationPage = lazy(() =>
  import('../components/publishing/QuotaRotationPage').then((m) => ({
    default: m.QuotaRotationPage,
  })),
);
// Recent Publishes — landed by T343 (Phase 5 Track B; carries E.9 verbatim).
const RecentPublishesPage = lazy(() =>
  import('../components/publishing/RecentPublishesPage').then((m) => ({
    default: m.RecentPublishesPage,
  })),
);
const AnalyticsPage = lazy(() =>
  import('../components/analytics/AnalyticsPage').then((m) => ({
    default: m.AnalyticsPage,
  })),
);
const AdDeliveryPerformancePage = lazy(() =>
  import('../components/analytics/AdDeliveryPerformancePage').then((m) => ({
    default: m.AdDeliveryPerformancePage,
  })),
);
const QualityPage = lazy(() =>
  import('../components/analytics/QualityPage').then((m) => ({
    default: m.QualityPage,
  })),
);
const CostPage = lazy(() =>
  import('../components/analytics/CostPage').then((m) => ({
    default: m.CostPage,
  })),
);
const ProviderSpendPage = lazy(() =>
  import('../components/analytics/ProviderSpendPage').then((m) => ({
    default: m.ProviderSpendPage,
  })),
);
const UptimeDetailPage = lazy(() =>
  import('../components/analytics/UptimeDetailPage').then((m) => ({
    default: m.UptimeDetailPage,
  })),
);
const PlayerHealthPage = lazy(() =>
  import('../components/analytics/PlayerHealthPage').then((m) => ({
    default: m.PlayerHealthPage,
  })),
);
// LiveMonitor (fleet view) — landed by T323, wired here by T324.
const LiveMonitorPage = lazy(() =>
  import('../components/operations/LiveMonitorPage').then((m) => ({
    default: m.LiveMonitorPage,
  })),
);
const LiveMonitorDrillPage = placeholder('Monitoring · Drill');
// Alerts page — landed by T341 to satisfy V.14 (data-testid="ack-alert"
// contract for the smoke double-ack-idempotency test).
const AlertsPage = lazy(() =>
  import('../components/operations/AlertsPage').then((m) => ({
    default: m.AlertsPage,
  })),
);
const NotificationPreferencesPage = placeholder('Alerts · Preferences');
// System Health — landed by T341 (cron-status grid + watchdog freshness).
const SystemHealthPage = lazy(() =>
  import('../components/operations/SystemHealthPage').then((m) => ({
    default: m.SystemHealthPage,
  })),
);
// Phase 7 — advanced operator pages (T351).
const EdgeMapPage = lazy(() =>
  import('../components/operations/EdgeMapPage').then((m) => ({
    default: m.EdgeMapPage,
  })),
);
const DeliveryLogPage = lazy(() =>
  import('../components/operations/DeliveryLogPage').then((m) => ({
    default: m.DeliveryLogPage,
  })),
);
const BackgroundTasksPage = lazy(() =>
  import('../components/operations/BackgroundTasksPage').then((m) => ({
    default: m.BackgroundTasksPage,
  })),
);
const CapacityPage = lazy(() =>
  import('../components/operations/CapacityPage').then((m) => ({
    default: m.CapacityPage,
  })),
);
const ManifestPage = lazy(() =>
  import('../components/operations/ManifestPage').then((m) => ({
    default: m.ManifestPage,
  })),
);
const SleepModePage = lazy(() =>
  import('../components/operations/SleepModePage').then((m) => ({
    default: m.SleepModePage,
  })),
);
const ErrorBudgetPage = lazy(() =>
  import('../components/operations/ErrorBudgetPage').then((m) => ({
    default: m.ErrorBudgetPage,
  })),
);
// Automations — landed by T341 (Automations Overview + Builder + Detail + Logs).
const AutomationsPage = lazy(() =>
  import('../components/operations/AutomationsPage').then((m) => ({
    default: m.AutomationsPage,
  })),
);
const AutomationBuilderPage = lazy(() =>
  import('../components/operations/AutomationBuilderPage').then((m) => ({
    default: m.AutomationBuilderPage,
  })),
);
const AutomationDetailPage = lazy(() =>
  import('../components/operations/AutomationDetailPage').then((m) => ({
    default: m.AutomationDetailPage,
  })),
);
const AutomationLogsPage = lazy(() =>
  import('../components/operations/AutomationLogsPage').then((m) => ({
    default: m.AutomationLogsPage,
  })),
);
// Version History — landed by T341 (VersionsPage + VersionDetailPage w/ DiffPane).
const VersionsPage = lazy(() =>
  import('../components/operations/VersionsPage').then((m) => ({
    default: m.VersionsPage,
  })),
);
const VersionDetailPage = lazy(() =>
  import('../components/operations/VersionDetailPage').then((m) => ({
    default: m.VersionDetailPage,
  })),
);
// Phase 7 — settings layout + 10 child sections (T351).
const SettingsLayout = lazy(() =>
  import('../components/settings/SettingsLayout').then((m) => ({
    default: m.SettingsLayout,
  })),
);
const OrganizationSection = lazy(() =>
  import('../components/settings/OrganizationSection').then((m) => ({
    default: m.OrganizationSection,
  })),
);
const MembersSection = lazy(() =>
  import('../components/settings/MembersSection').then((m) => ({
    default: m.MembersSection,
  })),
);
const ProfileSection = lazy(() =>
  import('../components/settings/ProfileSection').then((m) => ({
    default: m.ProfileSection,
  })),
);
const IntegrationsSection = lazy(() =>
  import('../components/settings/IntegrationsSection').then((m) => ({
    default: m.IntegrationsSection,
  })),
);
const SecuritySection = lazy(() =>
  import('../components/settings/SecuritySection').then((m) => ({
    default: m.SecuritySection,
  })),
);
const AuditLogPage = lazy(() =>
  import('../components/admin/AuditLogPage').then((m) => ({
    default: m.AuditLogPage,
  })),
);
const ChannelDefaultsSection = lazy(() =>
  import('../components/settings/ChannelDefaultsSection').then((m) => ({
    default: m.ChannelDefaultsSection,
  })),
);
const BrandingSection = lazy(() =>
  import('../components/settings/BrandingSection').then((m) => ({
    default: m.BrandingSection,
  })),
);
const BillingSection = lazy(() =>
  import('../components/settings/BillingSection').then((m) => ({
    default: m.BillingSection,
  })),
);
const NotificationsSection = lazy(() =>
  import('../components/settings/NotificationsSection').then((m) => ({
    default: m.NotificationsSection,
  })),
);

// Admin sub-tree — Phase 7 lands StreamKeyPage + StreamKeyRevealWizard (T351).
const OperatorPage = lazy(() =>
  import('../components/admin/OperatorPage').then((m) => ({
    default: m.OperatorPage,
  })),
);
const StreamKeyPage = lazy(() =>
  import('../components/admin/StreamKeyPage').then((m) => ({
    default: m.StreamKeyPage,
  })),
);
const StreamKeyRevealWizard = lazy(() =>
  import('../components/channels/StreamKeyRevealWizard').then((m) => ({
    default: m.StreamKeyRevealWizard,
  })),
);
const SlatesPage = lazy(() =>
  import('../components/admin/SlatesPage').then((m) => ({
    default: m.SlatesPage,
  })),
);

// Utility — landed by T363 (ComparePage, StatesPage, HelpPage, NotFoundPage).
const ComparePage = lazy(() =>
  import('../utility/ComparePage').then((m) => ({ default: m.ComparePage })),
);
const StatesPage = lazy(() =>
  import('../utility/StatesPage').then((m) => ({ default: m.StatesPage })),
);
const HelpPage = lazy(() =>
  import('../utility/HelpPage').then((m) => ({ default: m.HelpPage })),
);
const NotFoundPage = lazy(() =>
  import('../utility/NotFoundPage').then((m) => ({ default: m.NotFoundPage })),
);

// ---------------------------------------------------------------------------
// L — per-route wrapper.
// ---------------------------------------------------------------------------

function L({ children, name }: { children: ReactNode; name: string }): JSX.Element {
  return (
    <RouteErrorBoundary routeName={name}>
      <Suspense fallback={<PageSkeleton />}>{children}</Suspense>
    </RouteErrorBoundary>
  );
}

// ---------------------------------------------------------------------------
// The router definition.
// ---------------------------------------------------------------------------

export const router = createBrowserRouter([
  // Public routes
  { path: '/sign-in', element: <L name="SignIn"><SignInPage /></L> },
  { path: '/onboarding', element: <L name="Onboarding"><OnboardingPage /></L> },
  { path: '/verify-email', element: <L name="VerifyEmail"><VerifyEmailPage /></L> },
  { path: '/forgot-password', element: <L name="ForgotPassword"><ForgotPasswordPage /></L> },
  { path: '/locked', element: <L name="Locked"><LockedPage /></L> },
  { path: '/first-time', element: <L name="FirstTimeUser"><FirstTimeUserPage /></L> },
  { path: '/session-expired', element: <L name="SessionExpired"><SessionExpiredPage /></L> },

  // DEV-only — guarded by import.meta.env.DEV. Not registered in prod builds.
  ...(import.meta.env.DEV
    ? [
        {
          path: '/dev/primitives',
          element: <L name="DevPrimitives"><PrimitivesShowcasePage /></L>,
        },
      ]
    : []),

  // Protected routes
  {
    path: '/',
    element: <ProtectedShell />,
    children: [
      { index: true, element: <L name="Dashboard"><DashboardPage /></L> },

      // Channels
      { path: 'channels', element: <L name="Channels"><ChannelsListPage /></L> },
      { path: 'channels/new', element: <L name="CreateChannel"><CreateChannelPage /></L> },
      {
        path: 'channels/:channelId',
        element: <ChannelDetailLayout />,
        children: [
          { index: true, element: <Navigate to="overview" replace /> },
          { path: 'overview', element: <L name="ChOverview"><ChannelOverviewTab /></L> },
          { path: 'schedule', element: <L name="ChSchedule"><ChannelScheduleTab /></L> },
          { path: 'live', element: <L name="ChLive"><ChannelLiveTab /></L> },
          { path: 'health', element: <L name="ChHealth"><ChannelHealthTab /></L> },
          { path: 'settings', element: <L name="ChSettings"><ChannelSettingsTab /></L> },
          { path: 'watch', element: <L name="ChWatch"><ChannelWatchPage /></L> },
          { path: 'performance', element: <L name="ChPerf"><ChannelPerformancePage /></L> },
          { path: 'encoder', element: <L name="ChEncoder"><EncoderHelperPage /></L> },
          { path: 'go-live', element: <L name="ChGoLive"><GoLivePage /></L> },
          { path: 'destinations', element: <L name="ChDest"><DestinationsWizardPage /></L> },
          { path: 'transfer', element: <L name="ChTransfer"><ChannelTransferPage /></L> },
          { path: 'archive', element: <L name="ChArchive"><ChannelArchivePage /></L> },
          { path: 'duplicate', element: <L name="ChDup"><ChannelDuplicatePage /></L> },
        ],
      },

      // Library
      { path: 'library', element: <L name="Library"><LibraryPage /></L> },
      { path: 'library/upload', element: <L name="UploadAsset"><UploadAssetPage /></L> },
      { path: 'ingest-sources', element: <L name="IngestSources"><IngestSourcesPage /></L> },
      { path: 'library/captions-bulk', element: <L name="BulkCaptions"><BulkCaptionUploadPage /></L> },
      { path: 'library/smart-search', element: <L name="SmartSearch"><SmartSearchPage /></L> },
      { path: 'library/smart-breaks', element: <L name="SmartBreaks"><SmartBreakSuggestionsPage /></L> },
      { path: 'library/smart-breaks/:assetId', element: <L name="SmartBreaksAsset"><SmartBreakSuggestionsAssetPage /></L> },
      { path: 'library/scenes', element: <L name="Scenes"><ScenesPage /></L> },
      { path: 'library/:assetId', element: <L name="ContentDetail"><ContentDetailPage /></L> },
      { path: 'library/:assetId/markers', element: <L name="MarkerEditor"><MarkerEditorPage /></L> },
      { path: 'library/:assetId/analyse', element: <L name="AnalyseVideo"><AnalyseVideoPage /></L> },
      { path: 'library/:assetId/edit-metadata', element: <L name="EditMetadata"><EditMetadataPage /></L> },
      { path: 'library/:assetId/captions', element: <L name="AssetCaptions"><AssetCaptionsPage /></L> },
      { path: 'library/:assetId/replace', element: <L name="ReplaceFile"><ReplaceFilePage /></L> },

      // Schedules
      { path: 'schedules', element: <L name="Schedules"><SchedulesPage /></L> },
      { path: 'schedules/new', element: <L name="CreateSchedule"><CreateSchedulePage /></L> },
      { path: 'schedules/guide-export', element: <L name="GuideExport"><ProgrammeGuideExportPage /></L> },
      { path: 'schedules/:scheduleId', element: <L name="ScheduleEditor"><ScheduleEditorPage /></L> },
      { path: 'schedules/:scheduleId/publish', element: <L name="SchedulePublish"><PublishingModalPage /></L> },

      // Templates
      { path: 'templates', element: <L name="Templates"><TemplatesPage /></L> },
      { path: 'templates/:templateId', element: <L name="TemplateDetail"><TemplateDetailPage /></L> },

      // Guide + feeds
      { path: 'guide', element: <L name="Guide"><GuidePage /></L> },
      { path: 'feeds', element: <L name="Feeds"><FeedSourcesPage /></L> },

      // Audit log shortcut (canonical path is settings/security/audit-log;
      // /audit is the short alias linked from StreamKeyPage + Channel tabs).
      { path: 'audit', element: <L name="AuditShortcut"><AuditLogPage /></L> },

      // Editorial
      { path: 'editorial', element: <L name="Editorial"><EditorialPage /></L> },
      { path: 'editorial/inbox/:itemId', element: <L name="EditorialInbox"><EditorialInboxItemPage /></L> },
      { path: 'editorial/sync', element: <L name="EditorialSync"><EditorialSyncPage /></L> },
      { path: 'editorial/hmac-test', element: <L name="HmacTest"><HmacTestToolPage /></L> },

      // Compliance + batch
      { path: 'compliance', element: <L name="Compliance"><CompliancePage /></L> },
      { path: 'batch-config', element: <L name="BatchConfig"><BulkOpsPage /></L> },

      // Publishing
      { path: 'publishing', element: <L name="Publishing"><PublishingDestinationsPage /></L> },
      { path: 'publishing/quota-rotation', element: <L name="QuotaRotation"><QuotaRotationPage /></L> },
      { path: 'publishing/recent', element: <L name="RecentPublishes"><RecentPublishesPage /></L> },

      // Analytics
      { path: 'analytics', element: <L name="Analytics"><AnalyticsPage /></L> },
      { path: 'analytics/ad-delivery', element: <L name="AdDelivery"><AdDeliveryPerformancePage /></L> },
      { path: 'quality', element: <L name="Quality"><QualityPage /></L> },
      { path: 'cost', element: <L name="Cost"><CostPage /></L> },
      { path: 'cost/spend', element: <L name="ProviderSpend"><ProviderSpendPage /></L> },

      // Operational
      { path: 'uptime', element: <L name="Uptime"><UptimeDetailPage /></L> },
      { path: 'player-health', element: <L name="PlayerHealth"><PlayerHealthPage /></L> },
      { path: 'monitoring', element: <L name="Monitoring"><LiveMonitorPage /></L> },
      { path: 'monitoring/:channelId', element: <L name="MonitoringDrill"><LiveMonitorDrillPage /></L> },
      { path: 'alerts', element: <L name="Alerts"><AlertsPage /></L> },
      { path: 'alerts/preferences', element: <L name="NotifPrefs"><NotificationPreferencesPage /></L> },

      // System health
      { path: 'system-health', element: <L name="SystemHealth"><SystemHealthPage /></L> },
      { path: 'system-health/edge-map', element: <L name="EdgeMap"><EdgeMapPage /></L> },
      { path: 'system-health/delivery-path', element: <L name="DeliveryLog"><DeliveryLogPage /></L> },
      { path: 'system-health/background-tasks', element: <L name="BgTasks"><BackgroundTasksPage /></L> },
      { path: 'system-health/capacity', element: <L name="Capacity"><CapacityPage /></L> },
      { path: 'system-health/manifest', element: <L name="Manifest"><ManifestPage /></L> },
      { path: 'system-health/sleep', element: <L name="SleepMode"><SleepModePage /></L> },
      { path: 'system-health/error-budget', element: <L name="ErrorBudget"><ErrorBudgetPage /></L> },

      // Automations
      { path: 'automations', element: <L name="Automations"><AutomationsPage /></L> },
      { path: 'automations/new', element: <L name="AutomationsNew"><AutomationBuilderPage /></L> },
      { path: 'automations/:automationId', element: <L name="AutomationDetail"><AutomationDetailPage /></L> },
      { path: 'automations/:automationId/logs', element: <L name="AutomationLogs"><AutomationLogsPage /></L> },
      { path: 'automations/:automationId/edit', element: <L name="AutomationEdit"><AutomationBuilderPage /></L> },

      // Version history
      { path: 'version-history', element: <L name="Versions"><VersionsPage /></L> },
      { path: 'version-history/:versionId', element: <L name="VersionDetail"><VersionDetailPage /></L> },

      // Settings
      {
        path: 'settings',
        element: <SettingsLayout />,
        children: [
          { index: true, element: <Navigate to="organization" replace /> },
          { path: 'organization', element: <L name="OrgSettings"><OrganizationSection /></L> },
          { path: 'members', element: <L name="MembersSettings"><MembersSection /></L> },
          { path: 'profile', element: <L name="ProfileSettings"><ProfileSection /></L> },
          { path: 'integrations', element: <L name="IntegrationsSettings"><IntegrationsSection /></L> },
          { path: 'security', element: <L name="SecuritySettings"><SecuritySection /></L> },
          { path: 'security/audit-log', element: <L name="AuditLog"><AuditLogPage /></L> },
          { path: 'channel-defaults', element: <L name="ChannelDefaults"><ChannelDefaultsSection /></L> },
          { path: 'branding', element: <L name="Branding"><BrandingSection /></L> },
          { path: 'billing', element: <L name="Billing"><BillingSection /></L> },
          { path: 'notifications', element: <L name="NotificationsSettings"><NotificationsSection /></L> },
        ],
      },

      // Admin
      {
        path: 'admin',
        element: <AdminGate />,
        children: [
          { path: 'operator', element: <L name="Operator"><OperatorPage /></L> },
          { path: 'stream-keys', element: <L name="StreamKeys"><StreamKeyPage /></L> },
          {
            path: 'stream-keys/:channelId/reveal',
            element: <L name="StreamKeyReveal"><StreamKeyRevealWizard /></L>,
          },
          { path: 'slates', element: <L name="Slates"><SlatesPage /></L> },
        ],
      },

      // Utility
      { path: 'utility/compare', element: <L name="Compare"><ComparePage /></L> },
      { path: 'utility/states', element: <L name="States"><StatesPage /></L> },
      { path: 'help', element: <L name="Help"><HelpPage /></L> },
    ],
  },

  // 404 catch-all
  { path: '*', element: <L name="NotFound"><NotFoundPage /></L> },
]);

// Re-export React so `<React.Fragment>` / `<React.Suspense>` consumers don't
// need to add a separate import alongside the router.
export { React };
